import React, { useState, useEffect } from 'react';
import {
  Table,
  Button,
  Modal,
  Form,
  Input,
  Select,
  Space,
  Popconfirm,
  message,
  Card,
  Tag,
  Spin,
} from 'antd';
import { PlusOutlined, KeyOutlined, LockOutlined, UnlockOutlined, DeleteOutlined } from '@ant-design/icons';
import { getAdminList, createCampusAdmin, createUploader, resetPassword, updateAdminStatus, deleteAdmin } from '../../services/admin';
import { getAllCampuses } from '../../services/campus';
import { AdminAccount, Campus, PaginatedResponse } from '../../types';
import { useBreakpoint } from '../../hooks/useBreakpoint';

const { Option } = Select;

const AdminManagement: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedResponse<AdminAccount> | null>(null);
  const [campuses, setCampuses] = useState<Campus[]>([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [resetPasswordModalVisible, setResetPasswordModalVisible] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [currentAdmin, setCurrentAdmin] = useState<AdminAccount | null>(null);
  const [form] = Form.useForm();
  const [pagination, setPagination] = useState({ current: 1, pageSize: 10 });
  const { isMobile } = useBreakpoint();

  const fetchData = async (params?: any) => {
    setLoading(true);
    try {
      const [adminRes, campusRes] = await Promise.all([
        getAdminList({
          page: pagination.current,
          pageSize: pagination.pageSize,
          ...params,
        }),
        getAllCampuses(),
      ]);
      setData(adminRes);
      setCampuses(campusRes);
    } catch (error) {
      message.error('获取数据失败');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [pagination.current, pagination.pageSize]);

  const handleAdd = () => {
    form.resetFields();
    setModalVisible(true);
  };

  const handleSubmit = async (values: any) => {
    try {
      let res;
      if (values.role === 'uploader') {
        res = await createUploader(values.account, values.campusId);
        Modal.success({
          title: '上传专员账号创建成功',
          content: (
            <div>
              <p>账号：{res.account}</p>
              <p>初始密码：{res.initialPassword}</p>
              <p style={{ color: '#ff4d4f' }}>请保存好初始密码！</p>
              <p style={{ color: '#888', fontSize: 12 }}>上传专员权限：可上传学生信息和成绩信息，但不能审核成绩。</p>
            </div>
          ),
        });
      } else {
        res = await createCampusAdmin(values.account, values.campusId);
        Modal.success({
          title: '账号创建成功',
          content: (
            <div>
              <p>账号：{res.account}</p>
              <p>初始密码：{res.initialPassword}</p>
              <p style={{ color: '#ff4d4f' }}>请保存好初始密码！</p>
            </div>
          ),
        });
      }
      setModalVisible(false);
      fetchData();
    } catch (error) {
      message.error('创建失败');
    }
  };

  const handleResetPassword = async (admin: AdminAccount) => {
    try {
      const res = await resetPassword(admin.id);
      setNewPassword(res.newPassword);
      setCurrentAdmin(admin);
      setResetPasswordModalVisible(true);
      message.success('密码重置成功');
    } catch (error) {
      message.error('密码重置失败');
    }
  };

  const handleToggleStatus = async (admin: AdminAccount) => {
    const newStatus = admin.status === 'enabled' ? 'disabled' : 'enabled';
    try {
      await updateAdminStatus(admin.id, newStatus);
      message.success(newStatus === 'enabled' ? '账号已启用' : '账号已禁用');
      fetchData();
    } catch (error) {
      message.error('操作失败');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await deleteAdmin(id);
      message.success('删除成功');
      fetchData();
    } catch (error) {
      message.error('删除失败');
    }
  };

  const columns = [
    {
      title: '账号',
      dataIndex: 'account',
      key: 'account',
    },
    {
      title: '角色',
      dataIndex: 'role',
      key: 'role',
      render: (role: string) => {
        const roleMap: Record<string, { color: string; text: string }> = {
          super_admin: { color: 'red', text: '超级管理员' },
          campus_admin: { color: 'blue', text: '校区负责人' },
          uploader: { color: 'green', text: '上传专员' },
        };
        const { color, text } = roleMap[role] || { color: 'default', text: role };
        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: '所属校区',
      dataIndex: 'campusName',
      key: 'campusName',
      render: (campusName: string, record: AdminAccount) =>
        record.role === 'super_admin' ? '-' : campusName || '-',
    },
    {
      title: '状态',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => (
        <Tag color={status === 'enabled' ? 'success' : 'default'}>
          {status === 'enabled' ? '启用' : '禁用'}
        </Tag>
      ),
    },
    {
      title: '最后登录',
      dataIndex: 'lastLoginAt',
      key: 'lastLoginAt',
      render: (date: string) => date || '从未登录',
    },
    {
      title: '操作',
      key: 'action',
      render: (_: any, record: AdminAccount) => (
        <Space size="middle">
          <Button
            type="text"
            icon={<KeyOutlined />}
            onClick={() => handleResetPassword(record)}
          >
            重置密码
          </Button>
          {record.role !== 'super_admin' && (
            <>
              <Button
                type="text"
                icon={record.status === 'enabled' ? <LockOutlined /> : <UnlockOutlined />}
                onClick={() => handleToggleStatus(record)}
              >
                {record.status === 'enabled' ? '禁用' : '启用'}
              </Button>
              <Popconfirm
                title="确定删除该账号吗？"
                onConfirm={() => handleDelete(record.id)}
                okText="确定"
                cancelText="取消"
              >
                <Button type="text" danger icon={<DeleteOutlined />}>
                  删除
                </Button>
              </Popconfirm>
            </>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Card
        title="账号管理"
        extra={
          <Button type="primary" icon={<PlusOutlined />} onClick={handleAdd}>
            新建账号
          </Button>
        }
      >
        {isMobile ? (
          <Spin spinning={loading}>
            <div>
              {data?.list?.map((record: AdminAccount) => (
                <div key={record.id} style={{ marginBottom: 12 }}>
                  <Card size="small" style={{ width: '100%' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 8 }}>
                      <div>
                        <div style={{ fontWeight: 600, fontSize: 15 }}>{record.account}</div>
                        <div style={{ color: '#666', fontSize: 13, marginTop: 2 }}>
                          {record.role !== 'super_admin' ? `所属：${record.campusName || '-'}` : '全局管理员'}
                        </div>
                        <div style={{ color: '#999', fontSize: 12, marginTop: 2 }}>
                          最后登录：{record.lastLoginAt || '从未登录'}
                        </div>
                      </div>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 4, alignItems: 'flex-end' }}>
                        <Tag color={record.role === 'super_admin' ? 'red' : record.role === 'uploader' ? 'green' : 'blue'}>
                          {record.role === 'super_admin' ? '超级管理员' : record.role === 'uploader' ? '上传专员' : '校区负责人'}
                        </Tag>
                        <Tag color={record.status === 'enabled' ? 'success' : 'default'}>
                          {record.status === 'enabled' ? '启用' : '禁用'}
                        </Tag>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                      <Button size="small" icon={<KeyOutlined />} onClick={() => handleResetPassword(record)}>重置密码</Button>
                      {record.role !== 'super_admin' && (
                        <>
                          <Button
                            size="small"
                            icon={record.status === 'enabled' ? <LockOutlined /> : <UnlockOutlined />}
                            onClick={() => handleToggleStatus(record)}
                          >
                            {record.status === 'enabled' ? '禁用' : '启用'}
                          </Button>
                          <Popconfirm
                            title="确定删除该账号吗？"
                            onConfirm={() => handleDelete(record.id)}
                            okText="确定"
                            cancelText="取消"
                          >
                            <Button size="small" danger icon={<DeleteOutlined />}>删除</Button>
                          </Popconfirm>
                        </>
                      )}
                    </div>
                  </Card>
                </div>
              ))}
            </div>
          </Spin>
        ) : (
          <Table
            columns={columns}
            dataSource={data?.list}
            loading={loading}
            rowKey="id"
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: data?.total,
              onChange: (page, pageSize) => {
                setPagination({ current: page, pageSize: pageSize || 10 });
              },
            }}
          />
        )}
      </Card>

      {/* 新建账号弹窗 */}
      <Modal
        title="新建账号"
        open={modalVisible}
        forceRender
        onCancel={() => setModalVisible(false)}
        onOk={() => form.submit()}
        width={isMobile ? '100%' : 500}
        style={isMobile ? { top: 0, maxWidth: '100vw' } : {}}
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="role"
            label="账号类型"
            rules={[{ required: true, message: '请选择账号类型' }]}
            initialValue="campus_admin"
          >
            <Select placeholder="请选择账号类型">
              <Option value="campus_admin">校区负责人</Option>
              <Option value="uploader">上传专员</Option>
            </Select>
          </Form.Item>

          <Form.Item
            name="account"
            label="账号"
            rules={[
              { required: true, message: '请输入账号' },
              { min: 3, message: '账号至少3位' },
              { pattern: /^[a-zA-Z0-9_-]+$/, message: '账号只能包含字母、数字、下划线和横线' },
            ]}
          >
            <Input placeholder="请输入账号" />
          </Form.Item>

          <Form.Item
            name="campusId"
            label="所属校区"
            rules={[{ required: true, message: '请选择所属校区' }]}
          >
            <Select placeholder="请选择所属校区">
              {campuses.map((campus) => (
                <Option key={campus.id} value={campus.id}>
                  {campus.campusName}
                </Option>
              ))}
            </Select>
          </Form.Item>
        </Form>
      </Modal>

      {/* 重置密码弹窗 */}
      <Modal
        title="密码重置成功"
        open={resetPasswordModalVisible}
        onCancel={() => setResetPasswordModalVisible(false)}
        footer={[
          <Button key="ok" type="primary" onClick={() => setResetPasswordModalVisible(false)}>
            确定
          </Button>,
        ]}
      >
        <p>账号：{currentAdmin?.account}</p>
        <p>
          新密码：<strong style={{ color: '#ff4d4f', fontSize: 16 }}>{newPassword}</strong>
        </p>
        <p style={{ color: '#ff4d4f' }}>请保存好新密码！</p>
      </Modal>
    </div>
  );
};

export default AdminManagement;
